import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { apiService } from '../services/api';
import { Appointment, Notification } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import NavigationBar from '../components/NavigationBar';
import PermissionGate from '../components/PermissionGate';
import Footer from '../components/Footer';
import BookingStep3 from '../components/booking/BookingStep3';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import AppointmentActivitySection from '../components/AppointmentActivitySection';
import { ArrowLeft, Calendar, Clock, CheckCircle, X, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { getTotalDuration, getTotalPrice } from '../utils/appointmentHelpers';
import { localDateAndTimeToUtcIso } from '../utils/timezoneUtils';
import { DEFAULT_BOOKING_SLOT_MINUTES, resolveBookingSlotMinutes } from '../utils/bookingSlotSettings';

const BusinessRescheduleAppointmentPage: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [timeConflict, setTimeConflict] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Notification state for sidebar badge
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  
  // Business info state
  const [businessInfo, setBusinessInfo] = useState<any>(null);
  const [operatingHours, setOperatingHours] = useState<any[]>([]);
  
  // Appointment Summary collapse state (expanded by default)
  const [isAppointmentSummaryCollapsed, setIsAppointmentSummaryCollapsed] = useState(false);
  const [bookingSlotMinutes, setBookingSlotMinutes] = useState(DEFAULT_BOOKING_SLOT_MINUTES);
  
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };
  
  // Load unread notifications count for badge
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!currentBusiness?.id) return;
      try {
        // Make a lightweight call with limit 1 just to get the total count from pagination
        const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
        // Use pagination.total for the actual count, not the array length
        const totalUnread = response.pagination?.total || 0;
        // Set a dummy array with the count for compatibility, but we'll use totalUnread for display
        setUnreadNotifications(Array(totalUnread).fill(null) as Notification[]);
      } catch (error) {
        console.error('Error loading unread notifications count:', error);
        setUnreadNotifications([]);
      }
    };
    loadUnreadCount();
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!currentBusiness?.id) return;
    apiService
      .getAppointmentBookingSettings(currentBusiness.id)
      .then((d) => setBookingSlotMinutes(resolveBookingSlotMinutes(d)))
      .catch(() => setBookingSlotMinutes(DEFAULT_BOOKING_SLOT_MINUTES));
  }, [currentBusiness?.id]);
  
  // Extract professional IDs from appointment assignments
  const selectedProfessionals = React.useMemo(() => {
    if (!appointment?.assignments) return {};
    
    const professionals: { [serviceId: string]: any } = {};
    appointment.assignments.forEach(assignment => {
      if (assignment.professionalId && assignment.serviceId) {
        professionals[assignment.serviceId] = {
          id: assignment.professionalId,
          firstName: assignment.professionalFirstName,
          lastName: assignment.professionalLastName,
          email: assignment.professionalEmail
        };
      }
    });
    
    return professionals;
  }, [appointment]);
  
  const totalDuration = React.useMemo(() => getTotalDuration(appointment as any), [appointment]);
  
  // Load appointment details
  useEffect(() => {
    const loadAppointment = async () => {
      if (!appointmentId) {
        setError('Appointment ID is required');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const appointmentData = await apiService.getAppointment(appointmentId);
        setAppointment(appointmentData);
        
        // Set initial selected date/time from current appointment
        if (appointmentData.scheduledAt) {
          const scheduledDate = new Date(appointmentData.scheduledAt);
          setSelectedDate(scheduledDate);
          
          // Format time as HH:MM
          const hours = scheduledDate.getHours().toString().padStart(2, '0');
          const minutes = scheduledDate.getMinutes().toString().padStart(2, '0');
          setSelectedTime(`${hours}:${minutes}`);
        }
      } catch (error: any) {
        console.error('Error loading appointment:', error);
        setError(error.response?.data?.message || 'Failed to load appointment details');
        toast.error('Failed to load appointment details');
      } finally {
        setLoading(false);
      }
    };
    
    loadAppointment();
  }, [appointmentId]);

  // Load business info and operating hours
  useEffect(() => {
    const loadBusinessInfo = async () => {
      if (!appointment?.business_short_name && !appointment?.businessId) return;
      
      try {
        if (appointment.business_short_name) {
          const businessData = await apiService.getBusiness(appointment.business_short_name);
          const business = businessData?.business || businessData;
          setBusinessInfo(business);
          
          if (businessData?.operatingHours && Array.isArray(businessData.operatingHours)) {
            setOperatingHours(businessData.operatingHours);
          } else {
            const businessId = appointment.businessId || business?.id;
            if (businessId) {
              const hours = await apiService.getOperatingHours(businessId);
              setOperatingHours(hours || []);
            }
          }
        } else if (appointment.businessId) {
          const hours = await apiService.getOperatingHours(appointment.businessId);
          setOperatingHours(hours || []);
        }
      } catch (error) {
        console.error('Error loading business info:', error);
      }
    };
    
    if (appointment) {
      loadBusinessInfo();
    }
  }, [appointment]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTimeHeader = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
    return { dateStr, timeStr };
  };

  // Helper function to format time for display
  const formatTimeForDisplay = (time: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Helper function to format operating hours
  const formatOperatingHours = (hours: any[]): string => {
    if (!hours || hours.length === 0) return 'Hours not set';
    
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Group consecutive days with same hours
    const sortedHours = [...hours]
      .filter(h => !h.isClosed)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    
    if (sortedHours.length === 0) return 'Closed';
    
    // Simple format: show first and last day if all days are the same
    if (sortedHours.length === 7) {
      const first = sortedHours[0];
      const openTime = formatTimeForDisplay(first.openTime);
      const closeTime = formatTimeForDisplay(first.closeTime);
      return `Mon–Sun: ${openTime}–${closeTime}`;
    }
    
    // Show range if consecutive days have same hours
    let result = '';
    let startDay = sortedHours[0].dayOfWeek;
    let endDay = sortedHours[0].dayOfWeek;
    let currentOpen = sortedHours[0].openTime;
    let currentClose = sortedHours[0].closeTime;
    
    for (let i = 1; i < sortedHours.length; i++) {
      const hour = sortedHours[i];
      if (hour.openTime === currentOpen && hour.closeTime === currentClose && hour.dayOfWeek === endDay + 1) {
        endDay = hour.dayOfWeek;
      } else {
        // Output current range
        if (result) result += ', ';
        if (startDay === endDay) {
          result += `${dayNames[startDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
        } else {
          result += `${dayNames[startDay]}–${dayNames[endDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
        }
        // Start new range
        startDay = hour.dayOfWeek;
        endDay = hour.dayOfWeek;
        currentOpen = hour.openTime;
        currentClose = hour.closeTime;
      }
    }
    
    // Output last range
    if (result) result += ', ';
    if (startDay === endDay) {
      result += `${dayNames[startDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
    } else {
      result += `${dayNames[startDay]}–${dayNames[endDay]}: ${formatTimeForDisplay(currentOpen)}–${formatTimeForDisplay(currentClose)}`;
    }
    
    return result;
  };
  
  const handleReschedule = async () => {
    if (!appointment || !selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }
    
    if (timeConflict) {
      toast.error('The selected time has a conflict. Please choose another time.');
      return;
    }
    
    setSubmitting(true);
    try {
      // Staff selects date/time in *browser local timezone*; send an unambiguous UTC instant.
      const newScheduledAt = localDateAndTimeToUtcIso(selectedDate, selectedTime);
      
      // Call reschedule API
      await apiService.rescheduleAppointment(appointment.id, {
        scheduledAt: newScheduledAt
      });
      
      toast.success('Appointment rescheduled successfully!');
      
      // Navigate back using browser's back functionality
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error: any) {
      console.error('Error rescheduling appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to reschedule appointment');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">{error || 'Appointment not found'}</p>
            <button
              onClick={() => navigate('/dashboard/business?section=appointments')}
              className="text-blue-600 hover:text-blue-800"
            >
              Back to Appointments
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 no-animations flex flex-col">
      <NavigationBar />
      
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr]">
        <BusinessDashboardSidebar
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          activeSection="appointments"
          onClose={() => setIsSidebarOpen(false)}
          onToggleCollapse={toggleSidebar}
          unreadNotificationCount={unreadNotifications.length}
        />
        
        {/* Main Content */}
        <div className="flex flex-col flex-1">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="flex-1 text-center text-lg font-semibold text-gray-900">Reschedule Appointment</h1>
              {/* Invisible spacer to balance hamburger button and center the title */}
              <div className="w-10 h-10"></div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4 md:p-8">
            {/* Header */}
            <div className="mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Appointments
              </button>
              <h1 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900 mb-2">Reschedule Appointment</h1>
              <p className="hidden md:block text-gray-600">
                Select a new date and time for this appointment
              </p>
            </div>
            
            {/* Appointment Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              {/* Collapsible header for mobile */}
              <button
                onClick={() => setIsAppointmentSummaryCollapsed(!isAppointmentSummaryCollapsed)}
                className="w-full flex items-center justify-between mb-4 md:mb-4 md:pointer-events-none md:cursor-default"
              >
                <h3 className="text-lg font-semibold text-gray-900">Current Appointment</h3>
                <i className={`bi bi-chevron-down transition-transform duration-200 md:hidden text-gray-500 ${
                  isAppointmentSummaryCollapsed ? '' : 'rotate-180'
                }`}></i>
              </button>
              
              {/* Content - hidden in mobile when collapsed */}
              <div className={`md:block ${isAppointmentSummaryCollapsed ? 'hidden' : 'block'}`}>
                {/* Key-Value Pairs */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="bi bi-list-ul text-teal-600"></i>
                      <span>Items</span>
                    </div>
                    <div className="font-semibold text-sm">{appointment?.assignments?.length || 0} {appointment?.assignments?.length === 1 ? 'item' : 'items'}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="bi bi-hourglass-split text-purple-600"></i>
                      <span>Total Duration</span>
                    </div>
                    <div className="font-semibold text-sm">{getTotalDuration(appointment!)} min</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="bi bi-cash-coin text-green-600"></i>
                      <span>Total Price</span>
                    </div>
                    <div className="font-semibold text-sm font-mono">${getTotalPrice(appointment!).toFixed(2)}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="bi bi-info-circle text-blue-600"></i>
                      <span>Status</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      appointment?.status === 'CONFIRMED' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      appointment?.status === 'PENDING' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      appointment?.status === 'COMPLETED' ? 'bg-green-50 text-green-700 border-green-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                      {appointment?.status === 'CONFIRMED' ? 'Confirmed' :
                       appointment?.status === 'PENDING' ? 'Pending' :
                       appointment?.status === 'COMPLETED' ? 'Completed' :
                       'Canceled'}
                    </span>
                  </div>
                </div>

                <div className="h-px bg-gray-200 my-4"></div>

                {/* Created/Updated */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="bi bi-calendar-plus text-amber-600"></i>
                      <span>Created</span>
                    </div>
                    <div className="font-semibold text-sm">{appointment && formatDateTimeHeader(appointment.createdAt).dateStr}</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="bi bi-arrow-repeat text-amber-600"></i>
                      <span>Last Updated</span>
                    </div>
                    <div className="font-semibold text-sm">{appointment && formatDateTimeHeader(appointment.updatedAt).dateStr}</div>
                  </div>
                </div>

                <div className="h-px bg-gray-200 my-4"></div>

                {/* Activity Timeline (from appointment_events) */}
                <AppointmentActivitySection appointmentId={appointmentId ?? undefined} />
              </div>
            </div>
            
            {/* Date and Time Selection */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
              <BookingStep3
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onDateChange={setSelectedDate}
                onTimeChange={setSelectedTime}
                totalDuration={totalDuration}
                businessId={appointment.businessId}
                selectedProfessionals={selectedProfessionals}
                onTimeConflictChange={setTimeConflict}
                hideTotalDuration={true}
                hideCalendarLegend={true}
                hideAppointmentSummary={true}
                extendHours={true}
                bookingSlotMinutes={bookingSlotMinutes}
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleReschedule}
                disabled={!selectedDate || !selectedTime || timeConflict || submitting}
                className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Rescheduling...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Confirm Reschedule</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BusinessRescheduleAppointmentPage;

